import type { IconName } from '../Icons/Icons';
import FAIcon, { IconStyle } from '../Icons/Icons';
import { Theme } from '../types/enums';
import { type ProgressTrackerPropTypes, type ProgressTrackerStage } from './types/propsType';
import styles from './ProgressTracker.module.scss';
import {
  ProgressTrackerDesign,
  ProgressTrackerStatus,
  ProgressTrackerDividerStatus,
  ProgressTrackerDividerAlignment,
} from './types/enums';

const getIconStyle = (status: string, iconStyle?: IconStyle): IconStyle => {
  if (iconStyle) {
    return iconStyle;
  }
  return status === ProgressTrackerStatus.CURRENT || status === ProgressTrackerStatus.COMPLETED
    ? IconStyle.SOLID
    : IconStyle.REGULAR;
};
const getDividerStyle = (status: ProgressTrackerStatus, dividerStatus?: string): string => {
  if (dividerStatus) {
    return dividerStatus;
  }
  return status === ProgressTrackerStatus.COMPLETED
    ? ProgressTrackerDividerStatus.COMPLETED_DIVIDER
    : ProgressTrackerDividerStatus.DASHED_DIVIDER;
};
const getIconColor = (stage: ProgressTrackerStatus, color?: string): string => {
  if (color) {
    return color;
  }
  switch (stage) {
    case ProgressTrackerStatus.COMPLETED:
      return styles.amwayWhite;
    case ProgressTrackerStatus.CURRENT:
      return styles.darkPurple;
    default:
      return styles.darkGrayOnWhite;
  }
};
const { CURRENT, COMPLETED, COMPLETED_GRAY, FUTURE } = ProgressTrackerStatus;
const stageIconObj: Record<ProgressTrackerStatus, IconName> = {
  [CURRENT]: 'circle',
  [COMPLETED]: 'check',
  [COMPLETED_GRAY]: 'check',
  [FUTURE]: 'circle',
};
const ProgressTrackerVertical = ({ stages }: ProgressTrackerPropTypes) => (
  <section className={styles['progress-tracker']} data-testid="progress-tracker">
    {stages.map((stage, index) => (
      <section key={stage.status} className={styles['progress-tracker-stage']}>
        <div className={styles['progress-tracker-stage-left']}>
          <div
            className={`${styles['progress-tracker-node']} ${styles[stage.status]} ${
              index === stages.length - 1 && styles['final']
            }`}
            data-testid="progress-tracker-node"
            style={{
              backgroundColor: stage.bgColor,
              borderColor: stage.borderColor,
            }}
          >
            {stage.status === ProgressTrackerStatus.CURRENT && !stage.iconName?.length ? (
              <div
                style={{ backgroundColor: getIconColor(stage.status, stage.iconColor) }}
                className={styles['inner-circle']}
                data-testid="current-status-node"
              />
            ) : (
              <FAIcon
                theme={index === stages.length - 1 ? Theme.WHITE : Theme.GRAY}
                iconName={stage.iconName || stageIconObj[stage.status]}
                iconStyle={getIconStyle(stage.status, stage.iconStyle)}
                style={{ color: stage.iconColor }}
              />
            )}
          </div>
          <div className={`${styles['progress-tracker-bridge']} ${index === 0 ? styles['hide'] : ''}`} />
        </div>
        <div
          className={`${styles['progress-tracker-stage-right']} ${styles[stage.status]} ${
            stage.status === ProgressTrackerStatus.COMPLETED && index === stages.length - 1 && styles['final']
          }`}
          style={{
            color: stage.textColor,
          }}
        >
          <span className={`${styles['progress-tracker-stage-title']}`}>{stage.title}</span>
          {stage.desc}
        </div>
      </section>
    ))}
  </section>
);
const ProgressTrackerHorizontal = ({
  stages,
  dividerAlignment = ProgressTrackerDividerAlignment.DEFAULT,
}: ProgressTrackerPropTypes) => (
  <section
    className={`${styles[`progress-tracker-horizontal`]} ${styles[dividerAlignment]} ${dividerAlignment}`}
    data-testid="progress-traker-horizontal-testid"
  >
    {stages.map((stage: ProgressTrackerStage) => {
      const dividerStatus = getDividerStyle(stage.status, stage.dividerStatus);
      return (
        <div className={`${styles['stage-container']} ${styles[stage.status]}`} key={`${stage.status}-${stage.title}`}>
          <div
            className={styles['stage-body']}
            style={{
              backgroundColor: stage.bgColor,
              borderColor: stage.borderColor,
              color: stage.textColor,
            }}
          >
            <FAIcon
              iconName={stage.iconName || stageIconObj[stage.status]}
              iconStyle={getIconStyle(stage.status, stage.iconStyle)}
              style={{ color: getIconColor(stage.status, stage.iconColor) }}
            />
            <section className={styles['stage-content-container']}>
              <article className={styles['stage-content-title']}>{stage.title}</article>
              <article>{stage.desc}</article>
            </section>
          </div>
          <div
            className={`${styles['track']} ${styles[dividerStatus]}`}
            style={stage.dividerColor ? { borderColor: stage.dividerColor } : undefined}
          />
        </div>
      );
    })}
  </section>
);
const ProgressTracker = (props: ProgressTrackerPropTypes) => {
  const Tracker =
    !props.progressTrackerDesign || props.progressTrackerDesign === ProgressTrackerDesign.VERTICAL
      ? ProgressTrackerVertical
      : ProgressTrackerHorizontal;
  return <Tracker {...props} />;
};
ProgressTracker.displayName = 'ProgressTracker';
export default ProgressTracker;
export { ProgressTrackerStatus, ProgressTrackerDesign, ProgressTrackerDividerStatus, ProgressTrackerDividerAlignment };
export type { ProgressTrackerStage };
